import { useState } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "@/components/Kpi";
import { MESI, num } from "@/lib/format";

export const pct = (v) => (v === null || v === undefined ? "–" : `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
export const meseBreve = (m) => (/^\d{4}-\d{2}$/.test(m) ? MESI[parseInt(m.slice(5), 10) - 1].slice(0, 3) : m);
const tipStyle = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "JetBrains Mono, monospace" };

export function Legenda({ voci, testId }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600" data-testid={testId}>
      {voci.map(([colore, label]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ background: colore }} /> {label}
        </span>
      ))}
    </div>
  );
}

/** Istogramma mensile cliccabile: `serie` = [{key, nome, colore, stack?}], onMese(mese) al click. */
export function IstogrammaMensile({ dati, serie, meseSelezionato, onMese, tooltip, etichetta, testId }) {
  return (
    <div className="h-80 sm:h-96" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={dati} margin={{ top: 18, right: 8, left: 0, bottom: 0 }} barCategoryGap="8%" barGap={2} maxBarSize={70}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="nome" tickFormatter={meseBreve} tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => num(v, 0)} />
          <Tooltip contentStyle={tipStyle} cursor={{ fill: "#F1F5F9" }} content={tooltip} />
          {serie.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.nome} stackId={s.stack} fill={s.colore} radius={s.radius ?? 0}
              cursor="pointer" onClick={(d) => onMese(d?.payload?.nome ?? d?.nome)}
              opacity={1} isAnimationActive={false}
              shape={(p) => <Barra {...p} attiva={!meseSelezionato || p.payload?.nome === meseSelezionato} />}>
              {s.etichetta && <LabelList dataKey={s.etichetta === true ? s.key : s.etichetta} position="top" formatter={(v) => (v ? num(v, 0) : "")}
                style={{ fontSize: 11, fill: s.colore, fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }} />}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      {etichetta && <p className="text-[11px] text-slate-400 mt-1">{etichetta}</p>}
    </div>
  );
}

function Barra({ x, y, width, height, fill, radius, attiva }) {
  if (!height || height <= 0) return null;
  return <rect x={x} y={y} width={width} height={height} fill={fill} rx={typeof radius === "number" ? radius : 0}
    opacity={attiva ? 1 : 0.35} style={{ transition: "opacity 200ms" }} />;
}

export function TooltipRighe({ active, label, righe }) {
  if (!active) return null;
  return (
    <div style={tipStyle} className="bg-white px-3 py-2 shadow-md">
      <div className="font-semibold text-slate-700 mb-1">{meseBreve(label)} {label?.slice(0, 4)}</div>
      {righe.map(([colore, nome, valore, extra]) => (
        <div key={nome} className="flex items-center gap-2 text-slate-600">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colore }} />
          <span className="flex-1">{nome}</span>
          <span className="font-semibold">{num(valore, 0)}</span>
          {extra && <span className="text-slate-400">({extra})</span>}
        </div>
      ))}
    </div>
  );
}

/** Tabella per fascia oraria del mese cliccato; `colonne` = [{key, label, classe?, perc?}]. */
export function TabellaFasce({ titolo, righe, colonne, onChiudi, testId }) {
  return (
    <Card className="p-0 overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">{titolo}</span>
        <button type="button" onClick={onChiudi} data-testid={`${testId}-chiudi`}
          className="text-xs text-slate-500 hover:text-slate-900 transition-colors duration-200">Chiudi ✕</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Fascia oraria</th>
              {colonne.map((c) => <th key={c.key} className="text-right px-3 py-2 font-medium">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {righe.map((r) => (
              <tr key={r.nome} className="border-t border-slate-100" data-testid={`${testId}-riga`}>
                <td className="px-4 py-1.5 text-slate-700">{r.nome}</td>
                {colonne.map((c) => (
                  <td key={c.key} className={`px-3 py-1.5 text-right font-mono ${c.classe || ""}`}>
                    {c.perc ? pct(r[c.key]) : num(r[c.key], 0)}
                    {c.percDi && <span className="text-slate-400 text-xs ml-1">({pct(r[c.percDi])})</span>}
                  </td>
                ))}
              </tr>
            ))}
            {!righe.length && <tr><td colSpan={colonne.length + 1} className="px-4 py-6 text-center text-xs text-slate-400">Nessuna telefonata nel mese</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Box riepilogo del periodo, a tutta larghezza: `voci` = [{key, label, valore, perc?, colore?, forte?}] */
export function BoxPeriodo({ titolo, voci, testId }) {
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">{titolo}</div>
      <div className="flex flex-wrap gap-x-10 gap-y-5">
        {voci.map((v) => (
          <div key={v.label} className={`space-y-1 shrink-0 ${v.forte ? "rounded-md bg-slate-50 -my-2 -mx-3 px-3 py-2" : ""}`} data-testid={`${testId}-${v.key}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5 whitespace-nowrap h-4">
              {v.colore && <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: v.colore }} />}
              {v.label}
            </div>
            {v.valore === null || v.valore === undefined
              ? <div className="font-mono font-extrabold text-2xl text-slate-900">{pct(v.perc)}</div>
              : <>
                <div className="font-mono font-extrabold text-2xl text-slate-900">{num(v.valore, 0)}</div>
                <div className="text-xs font-mono font-bold text-slate-500 h-4">{v.perc !== undefined ? pct(v.perc) : ""}</div>
              </>}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function useMeseSelezionato() {
  const [mese, setMese] = useState(null);
  return [mese, (m) => setMese((cur) => (cur === m ? null : m))];
}
